import { FatalError } from "@prismicio/editor-support/Error"
import { useRequest } from "@prismicio/editor-support/Suspense"
import {
  ActionList,
  ActionListItem,
  ActionListLabel,
  Box,
  type IconName,
  Separator,
  Skeleton,
} from "@prismicio/editor-ui"
import type { PropsWithChildren } from "react"
import { Link, type To, useMatch, useResolvedPath } from "react-router-dom"

import { useAiABMLandingPagesFlag } from "../app/integrations/AmplitudeExperiments"
import {
  getBaseRepositoryUrl,
  getRepositoryName,
  useRedirect,
  type UseRedirectOptions,
} from "../app/routing"
import { getAllEnvironments } from "../app/services/environments"
import {
  accessSettingsPermission,
  deleteRepositoryPermission,
  syncEnvPermission,
  updatePlanPermission,
} from "../app/services/permissions"
import { useRepositoryV2 } from "../app/services/repository"
import { useMyRole } from "../app/services/role"

export function SettingsPageMenu() {
  const environments = useRequest(getAllEnvironments, [])
  const repositoryId = getRepositoryName()
  const currentEnvironment = environments.results.find(
    (env) => env.domain === repositoryId,
  )
  if (currentEnvironment === undefined)
    throw new FatalError("No environment matches the repository's name.")

  const { data: repository } = useRepositoryV2()
  const quotas = repository?.quotas

  const repositoryRole = useMyRole({})
  const canAccessSettings = accessSettingsPermission[repositoryRole]
  const isABMLandingPagesEnabled =
    useAiABMLandingPagesFlag({ active: true }) === "on"
  const redirect = useRedirect(!canAccessSettings, useRedirectOptions)

  if (redirect) return null

  const isProd = currentEnvironment.kind === "prod"

  const baseRepoUrl = getBaseRepositoryUrl(repositoryId)

  const canUpdatePlan = updatePlanPermission[repositoryRole]
  const canSyncEnv = syncEnvPermission[repositoryRole]
  const canDeleteRepo = deleteRepositoryPermission[repositoryRole]

  const hasEnvs =
    quotas &&
    (quotas.maxNbEnvironments > 0 || quotas.personalEnvironmentsEnabled)
  const hasIntegrationFields = quotas?.integrationFieldsEnabled

  return (
    <ActionList gap={4} variant="compact">
      <ActionListLabel>General</ActionListLabel>
      <SettingsPageMenuItem
        authorized={isProd}
        iconName="prismic"
        to={`${baseRepoUrl}/settings/repository`}
      >
        Repository
      </SettingsPageMenuItem>
      <SettingsPageMenuItem
        iconName="people"
        to={`${baseRepoUrl}/settings/users`}
      >
        Users
      </SettingsPageMenuItem>
      <SettingsPageMenuItem
        authorized={isProd && canUpdatePlan}
        iconName="creditCard"
        to={`${baseRepoUrl}/settings/plan`}
      >
        Plans & Billing
      </SettingsPageMenuItem>
      <SettingsPageMenuItem
        authorized={isProd}
        iconName="receiptLong"
        to={`${baseRepoUrl}/settings/payment-history`}
      >
        Payment History
      </SettingsPageMenuItem>
      <SettingsPageMenuItem
        authorized={isProd}
        iconName="dataUsage"
        to="/settings/usage"
      >
        Usage Dashboard
      </SettingsPageMenuItem>

      {hasEnvs && (isProd || canSyncEnv) && (
        <>
          <SettingsPageMenuSeparator />

          <ActionListLabel>Environments</ActionListLabel>
        </>
      )}
      <SettingsPageMenuItem
        authorized={hasEnvs && isProd}
        iconName="groupWork"
        to={`${baseRepoUrl}/settings/environments`}
      >
        Environments
      </SettingsPageMenuItem>
      <SettingsPageMenuItem
        authorized={hasEnvs && !isProd && canSyncEnv}
        iconName="refresh"
        to={`${baseRepoUrl}/settings/sync`}
      >
        Synchronize
      </SettingsPageMenuItem>

      <SettingsPageMenuSeparator />

      <ActionListLabel>Configuration</ActionListLabel>
      <SettingsPageMenuItem
        iconName="security"
        to={`${baseRepoUrl}/settings/apps`}
      >
        API & Security
      </SettingsPageMenuItem>
      <SettingsPageMenuItem
        iconName="visibility"
        to={`${baseRepoUrl}/settings/previews`}
      >
        Previews
      </SettingsPageMenuItem>
      <SettingsPageMenuItem
        authorized={hasIntegrationFields}
        iconName="multipleStop"
        to={`${baseRepoUrl}/settings/integrationfields`}
      >
        Integration Fields
      </SettingsPageMenuItem>
      <SettingsPageMenuItem iconName="language" to="/settings/locales">
        Translations & Locales
      </SettingsPageMenuItem>
      {isABMLandingPagesEnabled && (
        <SettingsPageMenuItem iconName="autoFixHigh" to="/settings/agent">
          Prismic Agent
        </SettingsPageMenuItem>
      )}
      <SettingsPageMenuItem
        iconName="autorenew"
        to={`${baseRepoUrl}/settings/webhooks`}
      >
        Webhooks
      </SettingsPageMenuItem>

      {isProd && canDeleteRepo && (
        <>
          <SettingsPageMenuSeparator />

          <ActionListLabel>Misc</ActionListLabel>
        </>
      )}
      <SettingsPageMenuItem
        authorized={isProd && canDeleteRepo}
        iconName="warning"
        to={`${baseRepoUrl}/settings/reset`}
      >
        Danger Zone
      </SettingsPageMenuItem>
    </ActionList>
  )
}

type SettingsPageMenuItemProps = PropsWithChildren<{
  authorized?: boolean
  iconName: IconName
  to: To
}>

function SettingsPageMenuItem(props: SettingsPageMenuItemProps) {
  const { authorized = true, children, iconName, to } = props

  const resolved = useResolvedPath(to)
  const match = useMatch({ path: resolved.pathname, end: true })
  const selected = match !== null

  useRedirect(!authorized && selected, useRedirectOptions)
  if (!authorized) return null

  return (
    <Link to={to} style={{ textDecoration: "none" }}>
      <ActionListItem
        backgroundColor="transparent"
        startIcon={iconName}
        selected={selected}
      >
        {children}
      </ActionListItem>
    </Link>
  )
}

function SettingsPageMenuSeparator() {
  return <Separator style="dashed" sx={{ marginBlock: 20 }} />
}

export function SettingsPageMenuSkeleton() {
  return (
    <ActionList gap={4} variant="compact">
      <SettingsPageMenuLabelSkeleton />
      <SettingsPageMenuItemSkeleton width={174} />
      <SettingsPageMenuItemSkeleton width={142} />
      <SettingsPageMenuItemSkeleton width={174} />
      <SettingsPageMenuItemSkeleton width={142} />
      <SettingsPageMenuItemSkeleton width={174} />

      <SettingsPageMenuSeparator />

      <SettingsPageMenuLabelSkeleton />
      <SettingsPageMenuItemSkeleton width={174} />

      <SettingsPageMenuSeparator />

      <SettingsPageMenuLabelSkeleton />
      <SettingsPageMenuItemSkeleton width={174} />
      <SettingsPageMenuItemSkeleton width={142} />
      <SettingsPageMenuItemSkeleton width={174} />
      <SettingsPageMenuItemSkeleton width={142} />
      <SettingsPageMenuItemSkeleton width={174} />

      <SettingsPageMenuSeparator />

      <SettingsPageMenuLabelSkeleton />
      <SettingsPageMenuItemSkeleton width={174} />
    </ActionList>
  )
}

function SettingsPageMenuLabelSkeleton() {
  return <Skeleton width={85} height={16} sx={{ marginBottom: 12 }} />
}

interface SettingsPageMenuItemSkeletonProps {
  width: number
}

function SettingsPageMenuItemSkeleton(
  props: SettingsPageMenuItemSkeletonProps,
) {
  const { width } = props
  return (
    <Box alignItems="center" gap={8} padding={{ block: 10, left: 10 }}>
      <Skeleton width={20} height={20} />
      <Skeleton width={width} height={16} />
    </Box>
  )
}

const useRedirectOptions: UseRedirectOptions = {
  to: "/",
  title: "Forbidden",
  subtitle:
    "You do not have sufficient permissions to access this page. You have been redirected to the main space.",
}
